import { createFileRoute } from "@tanstack/react-router";
import { getDay, getHours } from "date-fns";
import { FC, useEffect, useMemo, useState } from "react";
import { SlideTransition } from "../../component/animations/SlideTransition";
import { FitToScreen } from "../../component/slideUtilities/fitToScreen";
import { copyText } from "../../functions/copyText";
import { renderSlide } from "../../functions/renderSlide";
import { useDevtools } from "../../hooks/devtools/devtoolsContext";
import { useWordpressPostData } from "../../hooks/useWordpressPostData";
import { useWordpressSlides } from "../../hooks/useWordpressSlides";
import { Slide } from "../../types/Slides";

export interface TextBlockSlideProps {
  omroep: string;
}

export function filterSlides(slides: Slide[], date: Date) {
  return (
    slides
      //Filter if slide are in the current hour
      .filter((slide) => {
        if (slide.toDate != null && new Date(slide.toDate).getTime() < date.getTime()) return false;
        if (slide.fromDate != null && new Date(slide.fromDate).getTime() > date.getTime()) return false;
        if (!slide.hasTimespan) return true;
        return slide.timespan.days.includes(getDay(date).toString()) && slide.timespan.hours.includes(getHours(date).toString());
      })
  );
}

export const KabelkrantPage = () => {
  const params = Route.useParams();
  return <Kabelkrant omroep={params.omroep} />;
};

export const Kabelkrant: FC<TextBlockSlideProps> = ({ omroep }) => {
  const [index, setIndex] = useState<number>(0);
  const { posts } = useWordpressPostData();
  const { slides } = useWordpressSlides(posts);
  const devtools = useDevtools();

  const [currentSlides, setCurrentSlides] = useState(filterSlides(slides, new Date()));
  const [isPaused, setIsPaused] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  function updateSlides() {
    setCurrentSlides(filterSlides(slides, new Date()));
  }

  useEffect(() => {
    if (currentSlides.length === 0 && slides.length > 0) {
      setIsPaused(true);
      updateSlides();
    }
  }, [slides, currentSlides, index]);

  useEffect(() => {
    if (!isPaused) {
      return;
    }
    nextSlide();
    setIsPaused(false);
  }, [currentSlides, index]);

  function nextSlide() {
    setIndex((index) => {
      const newIndex = (index + 1) % currentSlides.length;
      const validNewIdex = isNaN(newIndex) ? 0 : newIndex;
      if (validNewIdex === 0) {
        updateSlides();
      }
      return validNewIdex;
    });
    setRenderKey((k) => k + 1);
  }

  const currentSlide = useMemo(() => currentSlides[index], [currentSlides, index]);

  return (
    <>
      {devtools.isDevtoolsOpen && (
        <div className="text-white">
          <button className="rounded-sm bg-slate-600 p-2" onClick={() => {
            copyText(JSON.stringify({
            currentSlide,
            posts,
            slides
          }))}}>
            Kopieer debug info
          </button>
          {isPaused ? "The slideshow is paused" : "The slideshow is running"}
        </div>
      )}

      <FitToScreen baseWidth={1920} baseHeight={1080}>
        <SlideTransition type="fade" divKey={`${JSON.stringify(currentSlide)}-${renderKey}`}>
          {currentSlide ? renderSlide(currentSlide, nextSlide) : <div style={{ color: "white" }}>Loading... </div>}
        </SlideTransition>
      </FitToScreen>
    </>
  );
};

export const Route = createFileRoute("/$omroep/")({
  component: KabelkrantPage
});
